/**
 * SoleadLab 2.0 - Monitoring Panel (Right Ticket Content)
 *
 * Renders ticket-specific content based on the active right rail ticket.
 * SCADA monitoring builder with site selection, data points, alarms,
 * and SLD diagrams. Wired to real monitoringStore with Zod-typed config
 * and new monitoring UI modals.
 */

import { useState, useEffect, useMemo } from 'react';
import { useMonitoringStore, useUIStore, useProjectStore } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { Button } from '../common';
import DataPointCard from './DataPointCard';
import UploadSchemaModal from './UploadSchemaModal';
import UploadDataModal from './UploadDataModal';
import ApiConfigModal from './ApiConfigModal';
import AlarmPanel from './AlarmPanel';
import AdvancedRulesPanel from './AdvancedRulesPanel';
import { SLDNewWindowPortal, SLDStandaloneViewer } from './SLDViewerModal';
import type { SLDDiagram } from '../../stores/monitoringStore';
import { ADVANCED_ALARM_DATAPOINT_ID } from '../../types/monitoring';
import type { TriggeredAdvancedRule } from '../../types/monitoring';
import './MonitoringPanel.css';

export default function MonitoringPanel({ activeTicket }: { activeTicket: string | null }) {
  // F-02E-2B: share viewers see only display data; data-source / sld /
  // server-sync tickets are full owner surfaces and are hidden. The
  // data-points and alarms tickets render their data; per-control
  // mutation buttons inside their subcomponents (acknowledge alarm,
  // edit datapoint, SLD edit, push data) remain visible — gating those
  // would require editing AlarmPanel / DataPointCard / SLD modal,
  // which are outside this unit's allowlist. Backend rejects every
  // mutation 401 in share mode. F-02E-2C will hide the inner buttons.
  const isReadOnly = useIsReadOnlyViewer();
  // Individual selectors — avoids full-store subscription that causes cascading re-renders
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);
  const selectSite = useMonitoringStore((s) => s.selectSite);
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const isConfigDirty = useMonitoringStore((s) => s.isConfigDirty);
  const isConfigLoading = useMonitoringStore((s) => s.isConfigLoading);
  const configVersion = useMonitoringStore((s) => s.configVersion); // B2 — CAS token; Publish is guarded when null
  const dataMode = useMonitoringStore((s) => s.dataMode);
  const setDataMode = useMonitoringStore((s) => s.setDataMode);
  const saveConfig = useMonitoringStore((s) => s.saveConfig);
  const schema = useMonitoringStore((s) => s.schema);
  const isSchemaLoaded = useMonitoringStore((s) => s.isSchemaLoaded);
  const runtimeStates = useMonitoringStore((s) => s.runtimeStates);
  const lastRefreshAt = useMonitoringStore((s) => s.lastRefreshAt);
  const isRefreshing = useMonitoringStore((s) => s.isRefreshing);
  const refreshData = useMonitoringStore((s) => s.refreshData);
  const isStreaming = useMonitoringStore((s) => s.isStreaming);
  const rawTriggeredAdvanced = useMonitoringStore((s) => s.triggeredAdvancedRules);
  const triggeredAdvancedRules = Array.isArray(rawTriggeredAdvanced) ? rawTriggeredAdvanced : [];
  const rawActiveAlarms = useMonitoringStore((s) => s.activeAlarms);
  const activeAlarms = Array.isArray(rawActiveAlarms) ? rawActiveAlarms : [];
  // Live-mode display overlay: matched offenders per advanced rule_id. Display-only.
  const advancedMatchedByRule = useMonitoringStore((s) => s.advancedMatchedByRule);
  const alarmSoundEnabled = useMonitoringStore((s) => s.alarmSoundEnabled);
  const setAlarmSoundEnabled = useMonitoringStore((s) => s.setAlarmSoundEnabled);
  const sldDiagrams = useMonitoringStore((s) => s.sldDiagrams);
  const providerStatus = useMonitoringStore((s) => s.providerStatus);
  const credentialStatus = useMonitoringStore((s) => s.credentialStatus);
  const providerHealth = useMonitoringStore((s) => s.providerHealth);
  const invalidDatapointIds = useMonitoringStore((s) => s.invalidDatapointIds);
  const startLive = useMonitoringStore((s) => s.startLive);
  const stopLive = useMonitoringStore((s) => s.stopLive);
  const refreshLive = useMonitoringStore((s) => s.refreshLive);
  const discoverSchemaAction = useMonitoringStore((s) => s.discoverSchema);
  const scadaConfig = useMonitoringStore((s) => s.scadaConfig);
  const setScadaConfig = useMonitoringStore((s) => s.setScadaConfig);
  const updateGlobals = useMonitoringStore((s) => s.updateGlobals);
  const setGlobalsLocal = useMonitoringStore((s) => s.setGlobalsLocal);
  const providers = useMonitoringStore((s) => s.providers);
  const loadProviders = useMonitoringStore((s) => s.loadProviders);

  const addNotification = useUIStore((s) => s.addNotification);
  const setAddMode = useUIStore((s) => s.setAddMode);

  // Modal states
  const [showUploadSchema, setShowUploadSchema] = useState(false);
  const [showUploadData, setShowUploadData] = useState(false);
  const [showApiConfig, setShowApiConfig] = useState(false);
  const [showAlarmPanel, setShowAlarmPanel] = useState(false);
  const [popupRule, setPopupRule] = useState<TriggeredAdvancedRule | null>(null);

  const [refreshInterval, setRefreshInterval] = useState(
    projectConfig?.globals?.fetchIntervalMs
      ? Math.round(projectConfig.globals.fetchIntervalMs / 1000)
      : scadaConfig.refreshInterval
  );
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isStartingLive, setIsStartingLive] = useState(false);

  // Sync dropdown when globals load from server
  useEffect(() => {
    const serverMs = projectConfig?.globals?.fetchIntervalMs;
    if (serverMs && serverMs >= 60_000) {
      setRefreshInterval(Math.round(serverMs / 1000));
    }
  }, [projectConfig?.globals?.fetchIntervalMs]);

  // Data point filter & search
  const [dpFilter, setDpFilter] = useState<'all' | 'alarms' | 'critical' | 'warning' | 'ok'>('all');
  const [dpSearch, setDpSearch] = useState('');

  // Auto-bind monitoring to the current project's siteId.
  // F-02E-2C: share visitors must never trigger the selectSite chain —
  // it fires /monitoring/config + /monitoring/credentials (401, never
  // shareReadable) and refreshData → /monitoring/data/latest (403 unless
  // monitoring exposed). The data points + alarms display data the
  // visitor needs has already been hydrated from the project blob via
  // projectStore.loadProject's monitoringConfig branch, so this auto-bind
  // is pure noise for visitors. Owner/authenticated callers are unchanged.
  const projectSiteId = useProjectStore((s) => s.meta.siteId);
  useEffect(() => {
    if (isReadOnly) return;
    if (projectSiteId && projectSiteId !== selectedSiteId) {
      selectSite(projectSiteId);
    }
  }, [projectSiteId, isReadOnly]);

  // Load provider metadata (rate limits + capabilities) so the API-budget
  // calculator renders accurately even when the credentials modal hasn't been
  // opened this session. loadProviders self-dedupes; the endpoint requires
  // auth, so skip it for share visitors.
  useEffect(() => {
    if (isReadOnly) return;
    loadProviders();
  }, [isReadOnly]);

  // Data points from real config
  const datapoints = projectConfig?.datapoints ?? [];

  const filteredDatapoints = useMemo(() => {
    let list = datapoints;
    if (dpFilter !== 'all') {
      list = list.filter((dp) => {
        const st = runtimeStates.get(dp.id)?.state ?? 'no_data';
        if (dpFilter === 'alarms') return st === 'warning' || st === 'critical';
        return st === dpFilter;
      });
    }
    if (dpSearch.trim()) {
      const q = dpSearch.trim().toLowerCase();
      list = list.filter((dp) =>
        dp.display_name.toLowerCase().includes(q) ||
        dp.entity_id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [datapoints, dpFilter, dpSearch, runtimeStates]);

  const advCritical = triggeredAdvancedRules.filter((r) => r.severity === 'critical');
  const advWarning = triggeredAdvancedRules.filter((r) => r.severity === 'warning');
  // Advanced-rule alarms raised by the live backend (SiteMonitor DO) arrive via
  // activeAlarms carrying the synthetic advanced datapoint_id. In upload mode the
  // same rules arrive via triggeredAdvancedRules (client-side eval). Dedupe by
  // rule_id so a rule present in both channels is counted/shown once.
  const triggeredRuleIds = new Set(triggeredAdvancedRules.map((r) => r.rule_id));
  // "Open" = active OR acknowledged (an acknowledged fault is still open, just
  // being worked on). Resolved/dismissed alarms are excluded from the active
  // surfaces and the open-count badge.
  const isOpenAlarm = (a: { state: string }) => a.state === 'active' || a.state === 'acknowledged';
  const liveAdvancedAlarms = activeAlarms.filter(
    (a) => a.datapoint_id === ADVANCED_ALARM_DATAPOINT_ID && isOpenAlarm(a)
      && !(a.rule_id && triggeredRuleIds.has(a.rule_id))
  );
  const dataPointOpenCount = activeAlarms.filter(
    (a) => isOpenAlarm(a) && a.datapoint_id !== ADVANCED_ALARM_DATAPOINT_ID
  ).length;
  const advancedAlarmCount = triggeredAdvancedRules.length + liveAdvancedAlarms.length;
  const totalAlarmCount = dataPointOpenCount + advancedAlarmCount;

  const handleAddDataPoint = () => {
    if (!selectedSiteId) {
      addNotification({ type: 'warning', title: 'No SCADA site linked to this project' });
      return;
    }
    if (!isSchemaLoaded) {
      addNotification({ type: 'warning', title: 'Upload a schema first (Data Source > Upload Schema)' });
      return;
    }
    addNotification({ type: 'info', title: 'Click on the map to place the data point' });
    setAddMode(true, 'dataPoint');
  };

  const handleClearDataPoints = async () => {
    if (!projectConfig) return;
    if (!confirm(`Remove all ${datapoints.length} data points?`)) return;
    // B3a — each delete is a version-gated canonical write, so run them SEQUENTIALLY: each
    // awaits the previous so it uses the version the prior delete returned (concurrent
    // deletes would race the version → 409). Stop on the first failure and report.
    const ids = datapoints.map((dp) => dp.id);
    let failed = false;
    for (const id of ids) {
      const ok = await useMonitoringStore.getState().removeDataPointConfig(id);
      if (!ok) { failed = true; break; }
    }
    addNotification(failed
      ? { type: 'error', title: 'Some data points could not be cleared', message: useMonitoringStore.getState().configError ?? 'Reloaded latest — please retry.' }
      : { type: 'info', title: 'Data points cleared' });
  };

  const handleManualRefresh = async () => {
    await refreshData();
    addNotification({ type: 'success', title: 'Data refreshed' });
  };

  const handleRefreshIntervalChange = async (value: number) => {
    const clamped = Math.max(60, Math.min(3600, value));
    setRefreshInterval(clamped);
    setScadaConfig({ refreshInterval: clamped });
    // B3a — persist ONLY the changed global via the canonical PATCH /monitoring/config/globals
    // (server shallow-merges; no whole-config write, no Publish). The DO picks up the new
    // interval immediately. updateGlobals applies the optimistic local merge itself.
    if (selectedSiteId) {
      const ok = await updateGlobals({ fetchIntervalMs: clamped * 1000 });
      if (ok) addNotification({ type: 'success', title: `Fetch interval set to ${clamped / 60} min` });
      else addNotification({ type: 'error', title: 'Could not save interval', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
    }
  };

  // Persist active-hours ("Limit fetch hours") changes to D1 + signal the DO
  // immediately, mirroring handleRefreshIntervalChange. Without this the change
  // only marked config dirty and never reached the DO until a manual Publish, so
  // the DO kept fetching/evaluating 24/7 (off-hours alarm noise). Spreads the
  // current globals so fetchIntervalMs (and any other field) survives the save,
  // and only writes to the server for complete/valid values to avoid persisting
  // partial input while the user is editing a time field.
  const saveActiveHours = async (nextActiveHours: {
    enabled: boolean; start: string; end: string; timezone: string;
  }) => {
    const validTime = (t: string) => /^\d{2}:\d{2}$/.test(t);
    // Disabled → no time values to validate. Enabled → require complete start+end.
    const complete = !nextActiveHours.enabled || (validTime(nextActiveHours.start) && validTime(nextActiveHours.end));
    // B3a — persist ONLY active_hours via the canonical PATCH /globals when the value is
    // complete/valid; otherwise keep it local-only (responsive inputs) until it is valid. No
    // whole-config write, no Publish.
    if (selectedSiteId && complete) {
      const ok = await updateGlobals({ active_hours: nextActiveHours });
      if (!ok) addNotification({ type: 'error', title: 'Could not save fetch hours', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
    } else {
      setGlobalsLocal({ active_hours: nextActiveHours });
    }
  };

  const handlePublishMonitoring = async () => {
    if (!isConfigDirty) {
      addNotification({ type: 'info', title: 'No changes to publish' });
      return;
    }
    // B2 — never send a legacy wholesale write. Without a loaded version we cannot CAS
    // safely, so block and reload from D1 rather than risk clobbering D1-only config.
    if (configVersion == null) {
      addNotification({ type: 'error', title: 'Config not loaded', message: 'Reloading from server — please retry Publish.' });
      if (selectedSiteId) await useMonitoringStore.getState().loadConfig(selectedSiteId);
      return;
    }
    const ok = await saveConfig(); // mode:'merge' + expectedVersion
    if (ok) {
      addNotification({ type: 'success', title: 'Monitoring config published' });
    } else {
      addNotification({
        type: 'error',
        title: 'Publish failed',
        message: useMonitoringStore.getState().configError ?? 'Please retry.',
      });
    }
  };

  const handlePullFromServer = async () => {
    if (!selectedSiteId) return;
    await useMonitoringStore.getState().loadConfig(selectedSiteId);
    addNotification({ type: 'success', title: 'Config pulled from server' });
  };

  const handleOpenSLD = (sldId: string) => {
    useUIStore.getState().openModal('sldViewer', { sldId });
  };

  // State for SLD diagrams opened in separate windows
  const [sldWindows, setSldWindows] = useState<SLDDiagram[]>([]);

  const handleOpenSLDNewTab = (sldId: string) => {
    const sld = sldDiagrams.find((s) => s.id === sldId);
    if (!sld) return;
    // Don't open duplicate windows for the same diagram
    if (sldWindows.some((w) => w.id === sld.id)) return;
    setSldWindows((prev) => [...prev, sld]);
  };

  const handleCloseSLDWindow = (sldId: string) => {
    setSldWindows((prev) => prev.filter((w) => w.id !== sldId));
  };

  const formatLastRefresh = () => {
    const ts = lastRefreshAt || scadaConfig.lastRefresh;
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString();
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="monitoring-panel">

      {/* ── DATA SOURCE ─────────────────────────── */}
      {/* F-02E-2B: entire data-source ticket is hidden for share viewers —
          monitoring/config + credentials + test-connection + push-data
          are all owner-only writes. */}
      {activeTicket === 'data-source' && !isReadOnly && (
        <div className="mp-ticket-content">
          {/* Connection status indicator */}
          <div className={`mp-connection-status ${isStreaming ? 'mp-connection-status--ok' : providerHealth?.status === 'circuit_open' ? 'mp-connection-status--warn' : providerStatus === 'connected' ? 'mp-connection-status--ok' : 'mp-connection-status--off'}`}>
            <span className={`status-dot ${isStreaming ? 'status-dot--success' : providerHealth?.status === 'circuit_open' ? 'status-dot--warning' : providerStatus === 'connected' ? 'status-dot--success' : 'status-dot--error'}`} />
            <span>
              {isStreaming ? 'Live (WebSocket)' :
               providerHealth?.status === 'circuit_open' ? 'Circuit Open' :
               providerHealth?.status === 'rate_limited' ? 'Rate Limited' :
               providerStatus === 'connected' ? 'Connected' :
               providerStatus === 'error' ? 'Error' : 'Disconnected'}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.7 }}>
              {dataMode === 'upload' ? 'Upload Mode' : 'Live Mode'}
            </span>
          </div>

          {/* Health warnings */}
          {providerHealth?.status === 'circuit_open' && (
            <div className="mp-health-warning mp-health-warning--circuit">
              Circuit breaker open — auto-retry in {providerHealth.circuitOpenUntil ? Math.max(0, Math.round((new Date(providerHealth.circuitOpenUntil).getTime() - Date.now()) / 60000)) : '?'} min
              {providerHealth.message && <div style={{ fontSize: 11, marginTop: 2 }}>{providerHealth.message}</div>}
            </div>
          )}
          {providerHealth?.status === 'error' && (
            <div className="mp-health-warning mp-health-warning--error">
              {providerHealth.message || 'Provider error'}
              {providerHealth.consecutiveFailures > 0 && ` (${providerHealth.consecutiveFailures} failures)`}
            </div>
          )}

          {/* Generic adapter warning */}
          {credentialStatus?.provider === 'generic' && dataMode === 'live' && (
            <div className="mp-health-warning mp-health-warning--generic">
              Generic provider — alarm accuracy not guaranteed
            </div>
          )}

          {/* Data Mode Toggle */}
          <div className="mp-section-label">Data Mode</div>
          <div style={{ display: 'flex', gap: 'var(--sl-space-2)' }}>
            <Button
              variant={dataMode === 'upload' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setDataMode('upload')}
            >
              Upload
            </Button>
            <Button
              variant={dataMode === 'live' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => {
                setDataMode('live');
                if (!credentialStatus?.hasCredentials) {
                  setShowApiConfig(true);
                }
              }}
            >
              Live API
            </Button>
          </div>

          {/* Schema */}
          <div className="mp-divider" />
          <div className="mp-section-label">Schema</div>
          {isSchemaLoaded && schema ? (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {schema.entities.length} entities{schema.detectedProvider ? ` (${schema.detectedProvider})` : ''}
            </div>
          ) : (
            <div className="api-settings__warning" style={{ fontSize: 11, padding: '2px 0' }}>
              No schema — upload JSON or discover devices
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--sl-space-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowUploadSchema(true)}>
              Upload Schema
            </Button>
            {dataMode === 'live' && credentialStatus?.hasCredentials && (
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  setIsDiscovering(true);
                  try {
                    await discoverSchemaAction();
                    addNotification({ type: 'success', title: 'Schema discovered from provider' });
                  } catch (err) {
                    addNotification({
                      type: 'error',
                      title: err instanceof Error ? err.message : 'Discovery failed',
                    });
                  } finally {
                    setIsDiscovering(false);
                  }
                }}
                disabled={isDiscovering}
                loading={isDiscovering}
              >
                Discover Devices
              </Button>
            )}
          </div>

          {/* Invalid datapoints warning */}
          {invalidDatapointIds.length > 0 && (
            <div className="mp-health-warning mp-health-warning--error" style={{ fontSize: 11 }}>
              {invalidDatapointIds.length} datapoint(s) have invalid entity/metric references after schema update
            </div>
          )}

          {/* Upload mode: measurement data */}
          {dataMode === 'upload' && (
            <>
              <div className="mp-divider" />
              <div className="mp-section-label">Sample Data</div>
              <Button variant="secondary" size="sm" fullWidth onClick={() => setShowUploadData(true)}>
                Upload Measurement Data
              </Button>
            </>
          )}

          {/* Live mode: API credentials + live controls */}
          {dataMode === 'live' && (
            <>
              <div className="mp-divider" />
              <div className="mp-section-label">API Credentials</div>
              {credentialStatus?.hasCredentials && (
                <div style={{ fontSize: 11, color: 'var(--green)' }}>
                  {credentialStatus.provider} credentials stored
                </div>
              )}
              <div style={{ display: 'flex', gap: 'var(--sl-space-2)' }}>
                <Button variant="secondary" size="sm" onClick={() => setShowApiConfig(true)}>
                  Configure API
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    const ok = await useMonitoringStore.getState().testConnection();
                    addNotification({
                      type: ok ? 'success' : 'error',
                      title: ok ? 'Connection successful' : 'Connection failed',
                    });
                  }}
                  disabled={!credentialStatus?.hasCredentials || providerStatus === 'testing'}
                >
                  {providerStatus === 'testing' ? 'Testing...' : 'Test Connection'}
                </Button>
              </div>

              {/* Go Live / Stop Live controls */}
              {credentialStatus?.hasCredentials && isSchemaLoaded && (
                <>
                  <div className="mp-divider" />
                  <div className="mp-section-label">Live Monitoring</div>
                  <div style={{ display: 'flex', gap: 'var(--sl-space-2)' }}>
                    {!isStreaming ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={async () => {
                          setIsStartingLive(true);
                          try {
                            await startLive();
                            addNotification({ type: 'success', title: 'Live monitoring started' });
                          } catch {
                            addNotification({ type: 'error', title: 'Failed to start live monitoring' });
                          } finally {
                            setIsStartingLive(false);
                          }
                        }}
                        disabled={isStartingLive}
                        loading={isStartingLive}
                      >
                        Go Live
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={async () => {
                          await stopLive();
                          addNotification({ type: 'info', title: 'Live monitoring stopped' });
                        }}
                      >
                        Stop Live
                      </Button>
                    )}
                    {isStreaming && (
                      <Button
                        variant="secondary"
                        size="sm"
                        title="Fetches fresh provider data and re-evaluates alarms; this can clear data-resolved alarms."
                        onClick={async () => {
                          await refreshLive();
                          addNotification({ type: 'success', title: 'Live data fetch triggered' });
                        }}
                      >
                        Fetch Live Data
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* Fetch Settings: Interval + Active Hours + Budget */}
          {dataMode === 'live' && credentialStatus?.hasCredentials && (
            <>
              {/* Fetch Settings: Interval + Active Hours + Budget */}
              <div className="mp-divider" />
              <div className="mp-section-label">Fetch Settings</div>
              <div className="auto-refresh">
                <span className={`auto-refresh__status ${isStreaming ? 'auto-refresh__status--live' : ''}`}>
                  {isStreaming ? 'Live' : 'Offline'}
                </span>
                <div className="auto-refresh__interval">
                  <select
                    value={refreshInterval}
                    onChange={(e) => handleRefreshIntervalChange(parseInt(e.target.value))}
                    className="form-input form-input--sm"
                  >
                    <option value={300}>5 min</option>
                    <option value={600}>10 min</option>
                    <option value={900}>15 min</option>
                  </select>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={projectConfig?.globals?.active_hours?.enabled ?? false}
                  onChange={(e) => {
                    const current = projectConfig?.globals?.active_hours;
                    saveActiveHours({
                      enabled: e.target.checked,
                      start: current?.start ?? '05:00',
                      end: current?.end ?? '21:00',
                      timezone: current?.timezone ?? 'Asia/Jerusalem',
                    });
                  }}
                />
                <span>Limit fetch hours</span>
              </label>
              {projectConfig?.globals?.active_hours?.enabled && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '4px 0' }}>
                  <input
                    type="time"
                    value={projectConfig.globals!.active_hours!.start}
                    onChange={(e) => {
                      saveActiveHours({ ...projectConfig!.globals!.active_hours!, start: e.target.value });
                    }}
                    style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={projectConfig.globals!.active_hours!.end}
                    onChange={(e) => {
                      saveActiveHours({ ...projectConfig!.globals!.active_hours!, end: e.target.value });
                    }}
                    style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                </div>
              )}

              {/* API Budget Calculator */}
              {(() => {
                const selectedProvider = providers.find((p) => p.name === credentialStatus?.provider);
                const bulkCalls = selectedProvider?.capabilities?.supportsBulkFetch
                  ? selectedProvider.capabilities.bulkCallsPerCycle
                  : undefined;

                const CORE_ENTITY_IDS = new Set(['site_overview', 'pv_array', 'grid', 'consumption', 'storage_system']);
                const coreCalls = 3;
                const detailEntityIds = new Set(
                  (projectConfig?.datapoints ?? [])
                    .map((dp) => dp.entity_id)
                    .filter((id) => !CORE_ENTITY_IDS.has(id))
                );
                const detailCalls = detailEntityIds.size;

                // Bulk-fetch providers (e.g. Meteocontrol) issue a FIXED number of
                // calls per cycle — one per device category — regardless of how many
                // data points/metrics are configured. Per-device providers scale with
                // the distinct non-core entities referenced by data points.
                const callsPerCycle = bulkCalls ?? (coreCalls + detailCalls);
                const callsDetail = bulkCalls != null
                  ? `bulk — ${bulkCalls} category call${bulkCalls === 1 ? '' : 's'}`
                  : `${coreCalls} core${detailCalls > 0 ? ` + ${detailCalls} device` : ''}`;

                const intervalMin = refreshInterval / 60;
                const activeHours = projectConfig?.globals?.active_hours;
                let activeMinutes = 24 * 60;
                if (activeHours?.enabled) {
                  const [sh, sm] = activeHours.start.split(':').map(Number);
                  const [eh, em] = activeHours.end.split(':').map(Number);
                  const startMin = sh * 60 + sm;
                  const endMin = eh * 60 + em;
                  activeMinutes = endMin > startMin ? endMin - startMin : (24 * 60 - startMin + endMin);
                }

                const cyclesPerDay = Math.ceil(activeMinutes / intervalMin);
                const estimatedCalls = cyclesPerDay * callsPerCycle;

                const dailyBudget = selectedProvider?.rateLimits?.callsPerDay;
                const pct = dailyBudget ? Math.round((estimatedCalls / dailyBudget) * 100) : null;
                const barColor = pct === null ? '#666' : pct > 90 ? '#ef4444' : pct > 70 ? '#eab308' : '#22c55e';

                return (
                  <div className="budget-calc">
                    <div className="budget-calc__row">
                      <span>Calls/cycle</span>
                      <span>{callsPerCycle} <span className="budget-calc__detail">({callsDetail})</span></span>
                    </div>
                    <div className="budget-calc__row">
                      <span>Cycles/day</span>
                      <span>{cyclesPerDay} <span className="budget-calc__detail">({Math.round(activeMinutes / 60)}h ÷ {intervalMin}min)</span></span>
                    </div>
                    <div className="budget-calc__row budget-calc__row--total">
                      <span>Est. daily</span>
                      <span>{estimatedCalls}{dailyBudget ? ` / ${dailyBudget}` : ''}</span>
                    </div>
                    {dailyBudget && (
                      <div className="budget-calc__bar-container">
                        <div
                          className="budget-calc__bar"
                          style={{ width: `${Math.min(pct!, 100)}%`, backgroundColor: barColor }}
                        />
                      </div>
                    )}
                    {pct !== null && pct > 90 && (
                      <div className="budget-calc__warning">
                        {pct > 100 ? 'Over budget — reduce interval or enable active hours' : 'Near budget limit'}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {/* Refresh */}
          <div className="mp-divider" />
          <div className="scada-source-actions">
            <button className="scada-btn" onClick={handleManualRefresh} disabled={isRefreshing} title="Reload cached view — reloads the last cached data; does not fetch from the provider or re-evaluate alarms">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={isRefreshing ? { animation: 'spin 1s linear infinite' } : {}}>
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
            {lastRefreshAt && (
              <span className="scada-last-refresh">Last: {formatLastRefresh()}</span>
            )}
          </div>
        </div>
      )}

      {/* ── DATA POINTS ─────────────────────────── */}
      {activeTicket === 'data-points' && (
        <div className="mp-ticket-content">
          <div className="mp-section-label">
            Data Points ({filteredDatapoints.length !== datapoints.length
              ? `${filteredDatapoints.length}/${datapoints.length}`
              : datapoints.length})
          </div>
          {/* F-02E-2B: + Add Point and Clear All mutate the project's
              monitoring config. Backend rejects writes from share viewers
              anyway, but the UI shouldn't even hint at them. Hidden in
              read-only mode; authenticated owner/operator unchanged. */}
          {!isReadOnly && (
            <div className="mp-button-row" data-testid="monitoring-datapoints-actions">
              <Button variant="secondary" size="sm" onClick={handleAddDataPoint}>
                + Add Point
              </Button>
              {datapoints.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearDataPoints}>
                  Clear All
                </Button>
              )}
            </div>
          )}

          {/* Filter & Search toolbar */}
          {datapoints.length > 0 && (
            <div className="dp-toolbar">
              <div className="dp-search">
                <svg className="dp-search__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="dp-search__input"
                  placeholder="Search points..."
                  value={dpSearch}
                  onChange={(e) => setDpSearch(e.target.value)}
                />
                {dpSearch && (
                  <button className="dp-search__clear" onClick={() => setDpSearch('')} type="button">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
              <div className="dp-filter">
                {(['all', 'alarms', 'critical', 'warning', 'ok'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`dp-filter__chip${dpFilter === f ? ' dp-filter__chip--active' : ''}`}
                    onClick={() => setDpFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'alarms' ? 'Alarms' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {filteredDatapoints.length > 0 ? (
            <div className="data-points-grid">
              {filteredDatapoints.map((dp) => (
                <DataPointCard
                  key={dp.id}
                  config={dp}
                  runtime={runtimeStates.get(dp.id)}
                />
              ))}
            </div>
          ) : datapoints.length > 0 ? (
            <p className="mp-empty-text">No points match filter</p>
          ) : (
            <p className="mp-empty-text">No data points configured</p>
          )}

        </div>
      )}

      {/* ── ALARMS ──────────────────────────────── */}
      {activeTicket === 'alarms' && (
        <div className="mp-ticket-content mp-ticket-content--split">
          {/* F-02E-2C: keep "Alarm Panel" entry-point button visible (its
              modal hides write controls per F-02E-2C AlarmPanel patch);
              hide the Sound toggle in share viewer (owner preference +
              unlocks audio context). */}
          <div className="alarm-controls">
            <Button variant="secondary" size="sm" onClick={() => setShowAlarmPanel(true)}>
              Alarm Panel ({totalAlarmCount})
            </Button>
            {!isReadOnly && (
              <label className="alarm-sound-toggle" data-testid="monitoring-alarms-sound-toggle">
                <input
                  type="checkbox"
                  checked={alarmSoundEnabled}
                  onChange={(e) => setAlarmSoundEnabled(e.target.checked)}
                />
                <span>Sound</span>
              </label>
            )}
          </div>

          {/* ── Raised Alarms (scrollable) ── */}
          <div className="mp-split-section">
            <div className="mp-section-label">Raised Alarms ({advancedAlarmCount})</div>
            <div className="mp-split-scroll">
              {advancedAlarmCount > 0 ? (
                <div className="alarms-list">
                  {[...advCritical, ...advWarning].map((rule) => (
                    <div
                      key={rule.rule_id}
                      className={`alarm-item alarm-item--compact alarm-item--${rule.severity}`}
                      onClick={() => setPopupRule(rule)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="alarm-item__header">
                        <span className={`alarm-item__severity alarm-item__severity--${rule.severity}`}>{rule.severity === 'critical' ? 'CRIT' : 'WARN'}</span>
                        <span className={`alarm-item__type alarm-item__type--${rule.rule_type}`}>
                          {rule.rule_type === 'zero_check' ? 'ZERO CHECK' : rule.rule_type === 'deviation' ? 'DEVIATION' : 'EXPRESSION'}
                        </span>
                      </div>
                      <div className="alarm-item__message">{rule.rule_name}</div>
                      <div className="alarm-item__details">
                        <span>{rule.message || rule.details}</span>
                      </div>
                    </div>
                  ))}
                  {/* Live-mode advanced alarms (raised by the backend DO). The
                      synthetic datapoint_id is hidden; the message already carries
                      the rule name + matched-metric summary. */}
                  {liveAdvancedAlarms.map((alarm) => {
                    const acked = alarm.state === 'acknowledged';
                    // Display-only offender preview recovered from the frontend
                    // evaluator overlay; keyed by rule_id, honors exclusions. Never
                    // an alarm source — the card itself is the DO-owned alarm.
                    const matched = alarm.rule_id ? advancedMatchedByRule?.[alarm.rule_id] : undefined;
                    return (
                    <div
                      key={alarm.id}
                      className={`alarm-item alarm-item--compact alarm-item--${alarm.severity}`}
                      style={acked ? { opacity: 0.55 } : undefined}
                    >
                      <div className="alarm-item__header">
                        <span className={`alarm-item__severity alarm-item__severity--${alarm.severity}`}>{alarm.severity === 'critical' ? 'CRIT' : 'WARN'}</span>
                        <span className="alarm-item__type">ADVANCED</span>
                        {acked && <span className="alarm-item__type">ACK</span>}
                      </div>
                      <div className="alarm-item__message">{alarm.message || `${alarm.metric} ${alarm.operator} ${alarm.threshold}`}</div>
                      {matched && matched.length > 0 ? (
                        <div className="alarm-item__details">
                          {matched.slice(0, 5).map((m, i) => (
                            <span key={i}>{m.entity_id}.{m.metric} = {m.value}</span>
                          ))}
                          {matched.length > 5 && <span>+{matched.length - 5} more</span>}
                        </div>
                      ) : (
                        <div className="alarm-item__details">
                          <span>{alarm.metric} {alarm.operator} {alarm.threshold}</span>
                        </div>
                      )}
                      {acked && (alarm.acknowledged_by || alarm.acknowledged_at) && (
                        <div className="alarm-item__details">
                          <span>Ack{alarm.acknowledged_by ? ` by ${alarm.acknowledged_by}` : ''}{alarm.acknowledged_at ? ` · ${new Date(alarm.acknowledged_at).toLocaleTimeString()}` : ''}</span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mp-empty-text">No active alarms</p>
              )}
            </div>
          </div>

          {/* ── Alarm Detail Popup ── */}
          {popupRule && (() => {
            // Parse details by rule type
            const d = popupRule.details;
            const isExpr = popupRule.rule_type === 'expression';
            const hasColon = d.includes(':');
            const hasPipe = d.includes(' | ');

            // Expression: "v1=100, v2=50 | cond1 OR cond2"
            // Zero/Deviation: "condition: match1, match2"
            let conditionText = '';
            let variables: { name: string; value: string }[] = [];
            let conditions: { expr: string; result: string }[] = [];

            if (isExpr && hasPipe) {
              const [varPart, condPart] = d.split(' | ');
              variables = varPart.split(', ').map((v) => {
                const [name, ...rest] = v.split('=');
                return { name: name.trim(), value: rest.join('=').trim() };
              });
              const combinator = condPart.includes(' AND ') ? ' AND ' : ' OR ';
              conditions = condPart.split(combinator).map((c) => {
                const arrow = c.lastIndexOf(' → ');
                if (arrow >= 0) {
                  return { expr: c.slice(0, arrow).trim(), result: c.slice(arrow + 3).trim() };
                }
                return { expr: c.trim(), result: '' };
              });
            } else if (hasColon) {
              conditionText = d.split(':')[0].trim();
            } else {
              conditionText = d;
            }

            return (
            <div className="alarm-popup-overlay" onClick={() => setPopupRule(null)}>
              <div className="alarm-popup" onClick={(e) => e.stopPropagation()}>
                <div className="alarm-popup__header">
                  <span>Alarm Detail</span>
                  <button className="alarm-popup__close" onClick={() => setPopupRule(null)}>&times;</button>
                </div>
                <div className="alarm-popup__body">
                  <div className="alarm-popup__badges">
                    <span className={`ap-card__severity ap-card__severity--${popupRule.severity}`}>
                      {popupRule.severity.toUpperCase()}
                    </span>
                    <span className={`alarm-item__type alarm-item__type--${popupRule.rule_type}`}>
                      {popupRule.rule_type === 'zero_check' ? 'ZERO CHECK' : popupRule.rule_type === 'deviation' ? 'DEVIATION' : 'EXPRESSION'}
                    </span>
                  </div>

                  <div className="alarm-popup__name">{popupRule.rule_name}</div>

                  {popupRule.message && (
                    <div className="alarm-popup__message">{popupRule.message}</div>
                  )}

                  {/* Zero check / Deviation: show condition */}
                  {conditionText && (
                    <div className="alarm-popup__condition">{conditionText}</div>
                  )}

                  {/* Expression: variables table */}
                  {variables.length > 0 && (
                    <>
                      <div className="alarm-popup__table-label">Variables</div>
                      <table className="alarm-popup__table">
                        <thead><tr><th>Variable</th><th>Value</th></tr></thead>
                        <tbody>
                          {variables.map((v, i) => (
                            <tr key={i}><td>{v.name}</td><td>{v.value}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* Expression: conditions table */}
                  {conditions.length > 0 && (
                    <>
                      <div className="alarm-popup__table-label" style={{ marginTop: 8 }}>Conditions</div>
                      <table className="alarm-popup__table">
                        <thead><tr><th>Expression</th><th>Result</th></tr></thead>
                        <tbody>
                          {conditions.map((c, i) => (
                            <tr key={i}>
                              <td>{c.expr}</td>
                              <td className={c.result === 'true' ? 'alarm-popup__result--true' : c.result === 'false' ? 'alarm-popup__result--false' : ''}>
                                {c.result}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* Matched metrics table (zero check / deviation) */}
                  {popupRule.matched_metrics && popupRule.matched_metrics.length > 0 && (
                    <>
                      <div className="alarm-popup__table-label" style={{ marginTop: 8 }}>
                        Matched Metrics ({popupRule.matched_metrics.length})
                      </div>
                      <table className="alarm-popup__table">
                        <thead><tr><th>Entity</th><th>Metric</th><th>Value</th></tr></thead>
                        <tbody>
                          {popupRule.matched_metrics.map((m, i) => (
                            <tr key={i}>
                              <td>{m.entity_id}</td>
                              <td>{m.metric}</td>
                              <td>{m.value ?? 'null'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          {/* ── Advanced Rules Config (scrollable) ──
              F-02E-2C: AdvancedRulesPanel hosts "+ Add Rule" + per-rule
              edit/delete write controls. Hidden entirely for share viewers;
              the alarms read-only display above is enough for visitors. */}
          {!isReadOnly && (
            <div className="mp-split-section" data-testid="monitoring-alarms-advanced-rules">
              <div className="mp-split-scroll">
                <AdvancedRulesPanel />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SLD ─────────────────────────────────── */}
      {/* F-02E-2B: SLD ticket has edit/import controls → hidden for viewers. */}
      {activeTicket === 'sld' && !isReadOnly && (
        <div className="mp-ticket-content">
          <div className="mp-section-label">Single Line Diagrams ({sldDiagrams.length})</div>
          <div className="mp-button-row">
            <Button variant="secondary" size="sm" onClick={() => useUIStore.getState().openModal('sldViewer')}>
              Open SLD
            </Button>
            <Button variant="ghost" size="sm" onClick={() => {
              if (sldDiagrams.length > 0) handleOpenSLDNewTab(sldDiagrams[0].id);
            }}>
              New Tab
            </Button>
          </div>

          {sldDiagrams.length > 0 ? (
            <div className="sld-list">
              {sldDiagrams.map((sld) => (
                <div key={sld.id} className="sld-item">
                  <button className="sld-item__main" onClick={() => handleOpenSLD(sld.id)}>
                    <span className="sld-item__name">{sld.name}</span>
                    <span className="sld-item__points">{sld.dataPointMappings.length} points</span>
                  </button>
                  <button
                    className="sld-item__popout"
                    onClick={(e) => { e.stopPropagation(); handleOpenSLDNewTab(sld.id); }}
                    title="Open in new window"
                  >
                    &#x29C9;
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mp-empty-text">No SLD diagrams</p>
          )}
        </div>
      )}

      {/* SLD window portals (rendered outside ticket conditional) */}
      {sldWindows.map((sld) => (
        <SLDNewWindowPortal
          key={`sld-win-${sld.id}`}
          title={`SLD — ${sld.name}`}
          diagram={sld}
          onClose={() => handleCloseSLDWindow(sld.id)}
        >
          <SLDStandaloneViewer diagram={sld} />
        </SLDNewWindowPortal>
      ))}

      {/* ── SERVER SYNC ─────────────────────────── */}
      {/* F-02E-2B: server-sync ticket is owner-only push/upload — hidden. */}
      {activeTicket === 'server-sync' && !isReadOnly && (
        <div className="mp-ticket-content">
          <div className="mp-section-label">Sync</div>
          {isConfigDirty && (
            <div className="api-settings__warning">
              You have unsaved changes. Publish to save to server.
            </div>
          )}
          {isConfigDirty && configVersion == null && (
            <div className="api-settings__warning">
              Config version not loaded yet — Publish is disabled until the server config finishes loading.
            </div>
          )}
          <Button
            variant="primary"
            size="sm"
            fullWidth
            onClick={handlePublishMonitoring}
            /* B2 — guard: no safe CAS write without a loaded version */
            disabled={!selectedSiteId || isConfigLoading || configVersion == null}
            loading={isConfigLoading}
          >
            Publish to Server
          </Button>
          <Button variant="ghost" size="sm" fullWidth onClick={handlePullFromServer}>
            Pull from Server
          </Button>
          {scadaConfig.lastSync && (
            <p className="mp-sync-time">
              Last sync: {new Date(scadaConfig.lastSync).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* ═══ MODALS ═══ */}
      <UploadSchemaModal
        isOpen={showUploadSchema}
        onClose={() => setShowUploadSchema(false)}
      />
      <UploadDataModal
        isOpen={showUploadData}
        onClose={() => setShowUploadData(false)}
      />
      <ApiConfigModal
        isOpen={showApiConfig}
        onClose={() => setShowApiConfig(false)}
      />
      <AlarmPanel
        isOpen={showAlarmPanel}
        onClose={() => setShowAlarmPanel(false)}
      />
    </div>
  );
}
